import { IPriceBreakdownList } from "@/features/assistant/types";
import { roundToTwoDecimals } from "./calculateQuoteTotals";

interface CalculateVssLineItemParams {
  itemPrice: IPriceBreakdownList;
  quantity: number;
  /** VAT % from account's dealer currency */
  vatPercentage?: number;
}

export interface VssLineItemCalculation {
  /** VSS price per unit (discount already applied to products in applyMargin) */
  vssUnitPrice: number;
  /** Discount amount per unit */
  vssDiscountPerUnit: number;
  /** Total discount for all quantities */
  vssDiscountAmount: number;
  /** VSS subtotal for all quantities (before VAT) */
  vssExtendedAmount: number;
  /** VAT on the VSS extended amount */
  vssVatAmount: number;
  /** Final VSS line total (vssExtendedAmount + vssVatAmount) */
  vssLineTotal: number;
}

export const calculateVssLineItem = ({
  itemPrice,
  quantity,
  vatPercentage = 0,
}: CalculateVssLineItemParams): VssLineItemCalculation => {
  const vssUnitPrice = roundToTwoDecimals(itemPrice.vssTotalPrice ?? 0);
  const vssFlatCharge = roundToTwoDecimals(itemPrice.vssFlatChargeTotal ?? 0);
  const vssDiscountPerUnit = roundToTwoDecimals(itemPrice.totalVssDiscountAmount ?? 0);

  // Flat surcharge must not be scaled by qty.
  const vssExtendedAmount = vssFlatCharge > 0
    ? roundToTwoDecimals((vssUnitPrice - vssFlatCharge) * quantity + vssFlatCharge)
    : roundToTwoDecimals(vssUnitPrice * quantity);
  const vssDiscountAmount = roundToTwoDecimals(vssDiscountPerUnit * quantity);
  const vssExtendedAfterDiscount = roundToTwoDecimals(vssExtendedAmount - vssDiscountAmount);
  const vssVatAmount = roundToTwoDecimals(vssExtendedAfterDiscount * (vatPercentage / 100));
  const vssLineTotal = roundToTwoDecimals(vssExtendedAfterDiscount + vssVatAmount);

  return {
    vssUnitPrice,
    vssDiscountPerUnit,
    vssDiscountAmount,
    vssExtendedAmount,
    vssVatAmount,
    vssLineTotal,
  };
};
